// src/components/shared/PhoneInput.jsx
// Shared phone number capture component — country code + number + label

import React, { useState, useEffect, useRef } from 'react';

export const PHONE_CODES = [
  { code: '+673', label: '🇧🇳 +673  Brunei' },
  { code: '+855', label: '🇰🇭 +855  Cambodia' },
  { code: '+62',  label: '🇮🇩 +62   Indonesia' },
  { code: '+856', label: '🇱🇦 +856  Laos' },
  { code: '+60',  label: '🇲🇾 +60   Malaysia' },
  { code: '+95',  label: '🇲🇲 +95   Myanmar' },
  { code: '+63',  label: '🇵🇭 +63   Philippines' },
  { code: '+65',  label: '🇸🇬 +65   Singapore' },
  { code: '+66',  label: '🇹🇭 +66   Thailand' },
  { code: '+670', label: '🇹🇱 +670  Timor-Leste' },
  { code: '+84',  label: '🇻🇳 +84   Vietnam' },
  { code: '+61',  label: '🇦🇺 +61   Australia' },
  { code: '+64',  label: '🇳🇿 +64   New Zealand' },
  { code: '+675', label: '🇵🇬 +675  Papua New Guinea' },
  { code: '+679', label: '🇫🇯 +679  Fiji' },
  { code: '+86',  label: '🇨🇳 +86   China' },
  { code: '+852', label: '🇭🇰 +852  Hong Kong' },
  { code: '+81',  label: '🇯🇵 +81   Japan' },
  { code: '+82',  label: '🇰🇷 +82   South Korea' },
  { code: '+886', label: '🇹🇼 +886  Taiwan' },
  { code: '+880', label: '🇧🇩 +880  Bangladesh' },
  { code: '+91',  label: '🇮🇳 +91   India' },
  { code: '+977', label: '🇳🇵 +977  Nepal' },
  { code: '+92',  label: '🇵🇰 +92   Pakistan' },
  { code: '+94',  label: '🇱🇰 +94   Sri Lanka' },
  { code: '+973', label: '🇧🇭 +973  Bahrain' },
  { code: '+964', label: '🇮🇶 +964  Iraq' },
  { code: '+962', label: '🇯🇴 +962  Jordan' },
  { code: '+965', label: '🇰🇼 +965  Kuwait' },
  { code: '+961', label: '🇱🇧 +961  Lebanon' },
  { code: '+968', label: '🇴🇲 +968  Oman' },
  { code: '+974', label: '🇶🇦 +974  Qatar' },
  { code: '+966', label: '🇸🇦 +966  Saudi Arabia' },
  { code: '+971', label: '🇦🇪 +971  UAE' },
  { code: '+967', label: '🇾🇪 +967  Yemen' },
  { code: '+44',  label: '🇬🇧 +44   United Kingdom' },
  { code: '+49',  label: '🇩🇪 +49   Germany' },
  { code: '+33',  label: '🇫🇷 +33   France' },
  { code: '+39',  label: '🇮🇹 +39   Italy' },
  { code: '+34',  label: '🇪🇸 +34   Spain' },
  { code: '+31',  label: '🇳🇱 +31   Netherlands' },
  { code: '+41',  label: '🇨🇭 +41   Switzerland' },
  { code: '+46',  label: '🇸🇪 +46   Sweden' },
  { code: '+1',   label: '🇺🇸 +1    United States' },
  { code: '+1',   label: '🇨🇦 +1    Canada' },
  { code: '+55',  label: '🇧🇷 +55   Brazil' },
  { code: '+52',  label: '🇲🇽 +52   Mexico' },
  { code: '+27',  label: '🇿🇦 +27   South Africa' },
  { code: '+234', label: '🇳🇬 +234  Nigeria' },
  { code: '+254', label: '🇰🇪 +254  Kenya' },
  { code: '+20',  label: '🇪🇬 +20   Egypt' },
  { code: '+7',   label: '🇷🇺 +7    Russia' },
];

export const PHONE_LABEL_OPTIONS = ['Mobile', 'Work', 'Telegram', 'WhatsApp', 'Signal'];

// Parse "+61 412345678" → { code: '+61', number: '412345678' }
// Returns { code: '', number: '' } for null/empty
export const parsePhoneStr = (str) => {
  if (!str) return { code: '', number: '' };
  const t = str.trim();
  if (t.startsWith('+')) {
    const sp = t.indexOf(' ');
    return sp > -1 ? { code: t.slice(0, sp), number: t.slice(sp + 1) } : { code: t, number: '' };
  }
  return { code: '', number: t };
};

// Join code + number → "+61 412345678", or "" if both empty
export const joinPhoneStr = (code, number) => {
  const c = (code || '').trim();
  const n = (number || '').trim();
  if (!c && !n) return '';
  if (!n) return c;
  if (!c) return n;
  return `${c} ${n}`;
};

/**
 * PhoneCapture — single row: [country code] [number] [label select or freetext]
 *
 * When "Other…" is selected the label select is replaced in-place by a
 * freetext input. Clicking the "←" button reverts back to the select.
 *
 * Props:
 *   code, number, label       — controlled values
 *   onCodeChange, onNumberChange, onLabelChange — change handlers
 *   disabled                  — locks all inputs
 */
const isPredefined = (v) => PHONE_LABEL_OPTIONS.includes(v);

export const PhoneCapture = ({ code, number, label, onCodeChange, onNumberChange, onLabelChange, disabled }) => {
  const [useCustom, setUseCustom] = useState(() => !!(label && !isPredefined(label)));

  // Sync useCustom when label is changed externally (e.g. cancel/reset)
  const prevLabelRef = useRef(label);
  useEffect(() => {
    const prev = prevLabelRef.current;
    prevLabelRef.current = label;
    if (label !== prev) {
      if (label && !isPredefined(label)) setUseCustom(true);
      else setUseCustom(false);
    }
  }, [label]);

  const selectVal = useCustom ? '__other__' : (isPredefined(label) ? label : '');
  const bg = disabled ? '#f4f7fb' : 'white';

  return (
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
      <select
        value={code || '+61'}
        onChange={e => onCodeChange(e.target.value)}
        disabled={disabled}
        className="form-input"
        style={{ width: 'auto', minWidth: '180px', flex: 'none', background: bg }}
      >
        {PHONE_CODES.map((p, i) => <option key={`${p.code}-${i}`} value={p.code}>{p.label}</option>)}
      </select>
      <input
        value={number || ''}
        onChange={e => onNumberChange(e.target.value)}
        placeholder="Local number (e.g. 412 345 678)"
        disabled={disabled}
        className="form-input"
        style={{ flex: 2, minWidth: '140px', background: bg }}
      />
      {useCustom ? (
        <div style={{ display: 'flex', gap: '4px', flex: 1, minWidth: '140px' }}>
          <input
            value={label || ''}
            onChange={e => onLabelChange(e.target.value)}
            placeholder="e.g. Home, Fax"
            disabled={disabled}
            className="form-input"
            style={{ flex: 1, background: bg }}
            autoFocus
          />
          {!disabled && (
            <button
              type="button"
              onClick={() => { setUseCustom(false); onLabelChange(''); }}
              title="Back to list"
              style={{ padding: '0 8px', background: 'none', border: '1px solid #d0daea', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', color: '#666', flexShrink: 0 }}
            >←</button>
          )}
        </div>
      ) : (
        <select
          value={selectVal}
          onChange={e => {
            if (e.target.value === '__other__') {
              setUseCustom(true);
            } else {
              setUseCustom(false);
              onLabelChange(e.target.value);
            }
          }}
          disabled={disabled}
          className="form-input"
          style={{ flex: 1, minWidth: '140px', background: bg }}
        >
          <option value="">— Label —</option>
          {PHONE_LABEL_OPTIONS.map(l => <option key={l} value={l}>{l}</option>)}
          <option value="__other__">Other…</option>
        </select>
      )}
    </div>
  );
};
